import { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory, useRouteMatch, useLocation } from "react-router-dom";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useToast } from "hooks/useToast";
import { emptyApi } from "store/apis/emptyApi";
import {
  useDeleteBillOrRequestMutation,
  useLazyDownloadBillOrRequestQuery,
  useUpdateBillOrRequestMutation,
  useLazyGetBillOrRequestQuery,
  useAddBillOrRequestMutation,
} from "store/apis/vendorBills";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { BILL_STATUS, FINANCIAL_RECORD_TYPE } from "constants/vendorBills";
import { BackendError } from "types/utils/error";
import { VendorBill } from "types/Models/vendorBills";
import { openLink } from "utils/openLink";

export const useBillActions = ({
  bill,
  onRecordPayment,
  isFromBillDetails,
}: {
  bill: VendorBill | undefined;
  onRecordPayment?: (billId: string) => void;
  isFromBillDetails?: boolean;
}) => {
  const billId = bill?.uuid;
  const isBill = bill?.bill_document_type === FINANCIAL_RECORD_TYPE.BILL;
  const dispatch = useDispatch();
  const history = useHistory();
  const { alertToast, successToast } = useToast();
  const { path } = useRouteMatch();
  const { search } = useLocation();
  const { link } = useConstructInternalLink();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [deleteBillOrRequest, deletingState] = useDeleteBillOrRequestMutation();
  const [update, updateState] = useUpdateBillOrRequestMutation();
  const [downloadBill, downloadState] = useLazyDownloadBillOrRequestQuery();
  const [getUrl, previewUrlState] = useLazyGetPreviewUrlQuery();
  const [getBill] = useLazyGetBillOrRequestQuery();
  const [createBill] = useAddBillOrRequestMutation();
  const [duplicating, setDuplicating] = useState(false);

  const onSendRequest = () => {
    if (!bill?.uuid) return;
    history.push(
      link(`${path}/send/${bill.uuid}`, {
        moreQuery: { BILL_TYPE: FINANCIAL_RECORD_TYPE.REQUEST },
      })
    );
  };

  const onStatusChange = async ({
    status,
  }: {
    status: keyof typeof BILL_STATUS;
  }) => {
    if (!billId) return;

    if (status === BILL_STATUS.PAID) {
      if (onRecordPayment) {
        onRecordPayment(billId);
      }
      return;
    }

    try {
      const payload = isBill
        ? {
            bill_payment_status: status,
          }
        : {
            bill_request_status: status,
          };

      await update({
        billId: billId!,
        entityId,
        groupId,
        payload: payload,
      }).unwrap();
      successToast({
        message: `Status updated to ${status.toLocaleLowerCase()}`,
      });
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError)?.data?.error?.message,
        },
        error as Error
      );
    }
  };

  const onDownload = async () => {
    if (!billId) return;

    try {
      const { doc_id } = await downloadBill({
        entityId,
        groupId,
        billId: billId!,
      }).unwrap();
      const { download_url } = await getUrl({
        fileId: doc_id,
        groupId,
      }).unwrap();
      window.open(download_url, "_blank");

      if (!bill?.is_document_uploaded) {
        dispatch(emptyApi.util.invalidateTags(["VENDOR_BILL"]));
      }
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError)?.data?.error?.message ??
            "Something went wrong",
        },
        error as Error
      );
    }
  };

  const onDelete = async () => {
    if (!billId) return;

    try {
      await deleteBillOrRequest({
        billId: billId!,
        groupId,
        entityId,
      }).unwrap();
      successToast({
        message: isBill ? "Bill deleted" : "Request deleted",
      });
    } catch (error) {
      alertToast(
        { message: (error as BackendError).data?.error?.message },
        error as Error
      );
    }
  };

  const onDuplicate = async () => {
    if (!billId || !bill?.bill_document_type) return;

    setDuplicating(true);
    try {
      const {
        uuid,
        bill_document,
        bill_payment_status,
        bill_request_status,
        invoice_items,
        bill_number,
        bill_number_without_prefix,
        invoice_number,
        invoice_number_without_prefix,
        ...currentBill
      } = await getBill({
        groupId,
        entityId,
        billId,
      }).unwrap();

      const newBill = await createBill({
        groupId,
        entityId,
        bill_type: bill.bill_document_type,
      }).unwrap();

      await update({
        groupId,
        entityId,
        billId: newBill.uuid,
        payload: {
          ...currentBill,
          invoice_items: invoice_items?.map((item) => ({
            ...item,
            uuid: undefined,
          })) as any,
        },
      }).unwrap();

      successToast({
        message: isBill
          ? "Bill duplicated successfully"
          : "Request duplicated successfully",
      });

      const billPath = isBill ? "bill" : "request";
      const navigateUrl = `/books/bills-and-vendors/${billPath}/${newBill.uuid}${search}`;

      if (isFromBillDetails) {
        openLink(navigateUrl);
      } else {
        history.push(navigateUrl);
      }
    } catch (error) {
      alertToast(
        { message: (error as BackendError)?.data?.error?.message },
        error as Error
      );
    } finally {
      setDuplicating(false);
    }
  };

  return {
    onSendRequest,
    onStatusChange,
    onDownload,
    onDelete,
    onDuplicate,
    isDownloading: downloadState.isFetching || previewUrlState.isFetching,
    isDeleting: deletingState.isLoading,
    isUpdatingStatus: updateState.isLoading,
    isDuplicating: duplicating,
  };
};
